import React, { useEffect, useMemo, useState } from "react";
import { getStoredAuthUser } from "../../../public/utils/authSession";
import { getAuthItem } from "../../../public/utils/authStorage";
import { getJwtRoleSlug } from "../../../public/utils/jwtRole";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";
import SafeMediaImage from "../../../global/SafeMediaImage";
import config from "../../../config/config";
import "../styles/ProfileSettingsPanel.css";

function initialsFrom(name, email) {
  const raw = String(name || "").trim();
  if (raw) {
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
    }
    return raw.slice(0, 2).toUpperCase();
  }
  const e = String(email || "").trim();
  return e ? e.slice(0, 2).toUpperCase() : "?";
}

function formatRoleLabel(roleSlug) {
  return String(roleSlug || "user")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function ProfileSettingsPanel() {
  const session = useMemo(() => {
    const stored = getStoredAuthUser() || null;
    const roleSlug =
      getJwtRoleSlug(getAuthItem("jwt_token")) || stored?.role || "";
    return {
      id: stored?.id ?? "",
      name: stored?.name || "Signed in user",
      email: stored?.email || "",
      roleSlug,
      initials: initialsFrom(stored?.name, stored?.email),
    };
  }, []);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${config.BASE_URL}/user/me`, {
          credentials: "include",
          headers: buildAuthHeaders(),
        });
        const payload = await res.json();
        if (!cancelled && payload?.success) {
          setProfile(payload.data);
        }
      } catch {
        // fall back to session data
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const p = profile || {};
  const name = p.name || session.name;
  const email = p.email || session.email || "Email not available";
  const roleLabel = formatRoleLabel(p.roleSlug || session.roleSlug);
  const initials = initialsFrom(name, email);
  const photoUrl = p.photo;

  const cards = [
    { label: "Display Name", value: name, tone: "primary" },
    { label: "Email Address", value: email, tone: "soft" },
    { label: "Role", value: roleLabel, tone: "soft" },
  ];

  if (p.contactNumber) {
    cards.push({ label: "Contact", value: p.contactNumber, tone: "soft" });
  }
  if (p.dateOfJoining) {
    cards.push({ label: "Date of Joining", value: formatDate(p.dateOfJoining), tone: "soft" });
  }
  if (p.address) {
    cards.push({ label: "Address", value: p.address, tone: "soft" });
  }

  return (
    <div className="profile-settings">
      <div className="profile-settings__mesh" aria-hidden />

      <header className="profile-settings__hero">
        <div className="profile-settings__hero-main">
          <p className="profile-settings__eyebrow">Account</p>
          <h2 className="profile-settings__title">Profile</h2>
          <p className="profile-settings__sub">
            Your current signed-in identity for this workspace.
          </p>
        </div>

        <div className="profile-settings__hero-card">
          {photoUrl ? (
            <SafeMediaImage
              src={photoUrl}
              alt={name}
              className="profile-settings__photo"
              fallbackClassName="profile-settings__photo-fallback"
              iconSize={20}
            />
          ) : (
            <span className="profile-settings__avatar" aria-hidden>
              {initials}
            </span>
          )}
          <div className="profile-settings__identity">
            <strong>{name}</strong>
            <span>{email}</span>
          </div>
          <span className="profile-settings__badge">{roleLabel}</span>
        </div>
      </header>

      {loading ? (
        <div className="profile-settings__loading">
          <span className="profile-settings__loading-bar" />
          Loading profile…
        </div>
      ) : (
        <>
          <section className="profile-settings__grid" aria-label="Profile details">
            {cards.map((card, index) => (
              <article
                key={card.label}
                className={`profile-settings__card${card.tone === "primary" ? " is-primary" : ""}`}
                style={{ "--ps-i": index }}
              >
                <span className="profile-settings__card-label">{card.label}</span>
                <strong className="profile-settings__card-value">{card.value}</strong>
              </article>
            ))}
          </section>

          <section className="profile-settings__panel">
            <div className="profile-settings__panel-head">
              <div>
                <p className="profile-settings__mini-eyebrow">Session</p>
                <h3>Active session details</h3>
              </div>
              <span className="profile-settings__status">
                <span className="profile-settings__status-dot" />
                Active
              </span>
            </div>

            <div className="profile-settings__facts">
              <div className="profile-settings__fact">
                <span className="profile-settings__fact-key">Source</span>
                <span className="profile-settings__fact-value">Stored login session</span>
              </div>
              <div className="profile-settings__fact">
                <span className="profile-settings__fact-key">Used by</span>
                <span className="profile-settings__fact-value">
                  Navigation, attendance, permissions, and all secured modules
                </span>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
